import { API_BASE, apiFetch } from "./client";
import type { components } from "./schema";

export type InviteCode = components["schemas"]["InviteCodeOut"];
export type InviteCodeCreated = components["schemas"]["InviteCodeCreated"];
export type InviteCodeCreate = components["schemas"]["InviteCodeCreate"];
export type AdminUser = components["schemas"]["AdminUser"];
export type ModeratorRecoveryCode = components["schemas"]["RecoveryCodeOut"];

export async function listInviteCodes(): Promise<InviteCode[]> {
  const body = await apiFetch<components["schemas"]["InviteCodeList"]>("/admin/invite-codes");
  return body.items;
}

export function createInviteCode(input: InviteCodeCreate): Promise<InviteCodeCreated> {
  return apiFetch<InviteCodeCreated>("/admin/invite-codes", { method: "POST", json: input });
}

export function revokeInviteCode(id: string): Promise<void> {
  return apiFetch<undefined>(`/admin/invite-codes/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Exact address only; the API has no partial search. */
export function findUser(email: string): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users?${new URLSearchParams({ email }).toString()}`);
}

export function issueRecoveryCode(userId: string): Promise<ModeratorRecoveryCode> {
  return apiFetch<ModeratorRecoveryCode>(`/admin/users/${encodeURIComponent(userId)}/recovery-code`, {
    method: "POST",
  });
}

export function setUserActive(userId: string, active: boolean): Promise<AdminUser> {
  const action = active ? "activate" : "deactivate";
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/${action}`, { method: "POST" });
}

export type Flag = components["schemas"]["FlagOut"];
export type FlagAction = components["schemas"]["FlagAction"];
export type FlagReason = components["schemas"]["FlagReason"];
export type FlagResolve = components["schemas"]["FlagResolve"];
export type FlaggedCompany = components["schemas"]["FlaggedCompany"];
export type CompanyReview = components["schemas"]["CompanyReview"];
export type AuditEntry = components["schemas"]["AuditEntry"];

export async function listFlags(state: "open" | "resolved"): Promise<Flag[]> {
  const body = await apiFetch<components["schemas"]["FlagList"]>(`/admin/flags?state=${state}`);
  return body.items;
}

export function resolveFlag(id: string, body: FlagResolve): Promise<Flag> {
  return apiFetch<Flag>(`/admin/flags/${encodeURIComponent(id)}/resolve`, { method: "POST", json: body });
}

export function reopenFlag(id: string): Promise<Flag> {
  return apiFetch<Flag>(`/admin/flags/${encodeURIComponent(id)}/reopen`, { method: "POST" });
}

export function mergeCompany(sourceId: string, intoId: string): Promise<FlaggedCompany> {
  return apiFetch<FlaggedCompany>(`/admin/companies/${encodeURIComponent(sourceId)}/merge`, {
    method: "POST",
    json: { into_id: intoId },
  });
}

export async function listCompaniesToReview(): Promise<FlaggedCompany[]> {
  const body = await apiFetch<components["schemas"]["ReviewList"]>("/admin/companies/review");
  return body.items;
}

export function reviewCompany(id: string, body: CompanyReview): Promise<FlaggedCompany> {
  return apiFetch<FlaggedCompany>(`/admin/companies/${encodeURIComponent(id)}`, { method: "PATCH", json: body });
}

export async function listAudit(limit = 100): Promise<AuditEntry[]> {
  const body = await apiFetch<components["schemas"]["AuditList"]>(`/admin/audit?limit=${limit}`);
  return body.items;
}

export type Stats = components["schemas"]["StatsOut"];
export type StatsPeriod = Stats["period"];

export function getStats(period: StatsPeriod): Promise<Stats> {
  return apiFetch<Stats>(`/admin/stats?period=${period}`);
}

/** A plain link: the browser downloads the CSV with the session cookie. */
export function exportUrl(period: StatsPeriod): string {
  return `${API_BASE}/admin/export.csv?period=${period}`;
}
